import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Page from "../components/Page";
import Select from "react-select";
import {
  IconPlus,
  IconSearch,
  IconFilter,
  IconCalendar,
  IconClipboardList,
  IconBuildingWarehouse,
  IconTruck,
  IconArrowRight,
  IconCheck,
  IconX,
  IconTrash,
  IconEdit,
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
  IconClock,
  IconAlertCircle,
  IconReceipt,
  IconEye,
} from "@tabler/icons-react";
import { iconStroke } from "../config/config";
import {
  useRequisitions,
  createRequisition,
  updateRequisition,
  cancelRequisition,
} from "../controllers/requisition.controller";
import { useInventoryItems } from "../controllers/inventory.controller";
import { useTheme } from "../contexts/ThemeContext";
import toast from "react-hot-toast";

const STATUS_CONFIG = {
  draft: { label: "Draft", badge: "badge-ghost", color: "text-gray-500" },
  pending_approval: { label: "Pending Approval", badge: "badge-warning", color: "text-amber-600" },
  partially_approved: { label: "Partially Approved", badge: "badge-info", color: "text-blue-500" },
  approved: { label: "Approved", badge: "badge-primary", color: "text-indigo-600" },
  rejected: { label: "Rejected", badge: "badge-error", color: "text-rose-600" },
  provider_ordered: { label: "Provider Ordered", badge: "badge-secondary", color: "text-purple-600" },
  provider_dispatched: { label: "Dispatched", badge: "badge-accent", color: "text-teal-600" },
  receiving: { label: "Receiving", badge: "badge-warning", color: "text-amber-500" },
  received: { label: "Received", badge: "badge-success", color: "text-emerald-600" },
  issued: { label: "Stock Issued", badge: "badge-success", color: "text-emerald-600" },
  cancelled: { label: "Cancelled", badge: "badge-neutral", color: "text-gray-400" },
};

export default function RequisitionsPage() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();

  // Filters state
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedMode, setSelectedMode] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingRequisition, setEditingRequisition] = useState(null);

  // Form state
  const [mode, setMode] = useState("self_managed");
  const [department, setDepartment] = useState("Kitchen");
  const [priority, setPriority] = useState("normal");
  const [neededByDate, setNeededByDate] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([
    { inventory_item_id: null, requested_quantity: 1, requested_unit: "", notes: "", itemOption: null },
  ]);

  // Data queries
  const { data: requisitions, total, isLoading, mutate } = useRequisitions({
    status: selectedStatus,
    mode: selectedMode,
    from: fromDate,
    to: toDate,
    search: searchQuery,
    page,
    limit: pageSize,
  });

  const { data: inventoryData } = useInventoryItems({ status: "all" });
  const rawItems = inventoryData?.items || inventoryData || [];
  const inventoryItems = Array.isArray(rawItems) ? rawItems : [];

  const inventoryOptions = useMemo(() => {
    return (inventoryItems || [])
      .filter((item) => item && item.id != null)
      .map((item) => ({
        value: item.id,
        label: `${item.title || "Item"} (${item.quantity ?? 0} ${item.unit || "unit"} in stock)`,
        item,
      }));
  }, [inventoryItems]);

  const totalPages = Math.max(1, Math.ceil((Number(total) || 0) / (Number(pageSize) || 20)));

  // Keep page within valid bounds when filters change
  React.useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const formatDateSafe = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return String(dateStr);
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (e) {
      return String(dateStr);
    }
  };

  // Reset form
  const resetForm = () => {
    setMode("self_managed");
    setDepartment("Kitchen");
    setPriority("normal");
    setNeededByDate("");
    setNotes("");
    setItems([
      { inventory_item_id: null, requested_quantity: 1, requested_unit: "", notes: "", itemOption: null },
    ]);
    setEditingRequisition(null);
  };

  const handleOpenCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleAddItemRow = () => {
    setItems([
      ...items,
      { inventory_item_id: null, requested_quantity: 1, requested_unit: "", notes: "", itemOption: null },
    ]);
  };

  const handleRemoveItemRow = (index) => {
    if (items.length === 1) {
      toast.error("At least one item is required");
      return;
    }
    const newItems = items.filter((_, idx) => idx !== index);
    setItems(newItems);
  };

  const handleItemChange = (index, field, value) => {
    if (!items[index]) return;
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    setItems(updated);
  };

  const handleItemSelect = (index, selectedOption) => {
    if (!items[index]) return;
    const updated = [...items];
    updated[index] = {
      ...updated[index],
      itemOption: selectedOption,
      inventory_item_id: selectedOption ? selectedOption.value : null,
      requested_unit: selectedOption?.item?.unit || "",
    };
    setItems(updated);
  };

  const handleSubmitRequisition = async (isDraft = false) => {
    // Validation
    const validItems = items.filter((it) => it.inventory_item_id && Number(it.requested_quantity) > 0);
    if (validItems.length === 0) {
      toast.error("Please add at least one valid item with a quantity greater than zero");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        mode,
        department,
        priority,
        needed_by_date: neededByDate || null,
        notes,
        is_draft: isDraft,
        items: validItems.map((it) => ({
          inventory_item_id: it.inventory_item_id,
          requested_quantity: Number(it.requested_quantity),
          requested_unit: it.requested_unit,
          notes: it.notes,
        })),
      };

      if (editingRequisition) {
        await updateRequisition(editingRequisition.id, payload);
        toast.success("Requisition updated successfully");
      } else {
        await createRequisition(payload);
        toast.success(isDraft ? "Draft saved successfully" : "Requisition submitted for approval");
      }

      setIsModalOpen(false);
      resetForm();
      mutate();
    } catch (err) {
      console.error("Submit requisition error:", err);
      toast.error(err.response?.data?.message || err.message || "Failed to submit requisition");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelRequisition = async (id, reqNo) => {
    const reason = window.prompt(`Enter reason for cancelling ${reqNo}:`);
    if (reason === null) return;

    try {
      await cancelRequisition(id, reason);
      toast.success("Requisition cancelled");
      mutate();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to cancel");
    }
  };

  // Custom react-select styles for dark mode compatibility
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: theme === "black" ? "#1e1e1e" : "#ffffff",
      borderColor: state.isFocused ? "#059669" : theme === "black" ? "#333333" : "#d1d5db",
      color: theme === "black" ? "#f3f4f6" : "#111827",
      minHeight: "38px",
      borderRadius: "0.5rem",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#059669",
      },
    }),
    singleValue: (base) => ({
      ...base,
      color: theme === "black" ? "#f3f4f6" : "#111827",
    }),
    input: (base) => ({
      ...base,
      color: theme === "black" ? "#f3f4f6" : "#111827",
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: theme === "black" ? "#1e1e1e" : "#ffffff",
      border: theme === "black" ? "1px solid #333333" : "1px solid #e5e7eb",
      zIndex: 9999,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#059669"
        : state.isFocused
        ? theme === "black"
          ? "#2a2a2a"
          : "#ecfdf5"
        : "transparent",
      color: state.isSelected
        ? "#ffffff"
        : theme === "black"
        ? "#f3f4f6"
        : "#111827",
      cursor: "pointer",
    }),
  };

  return (
    <Page className="p-4 md:p-6 space-y-6">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <IconClipboardList className="text-emerald-600" stroke={iconStroke} size={28} />
            Requisitions
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage ingredient requests, internal store issues, and integrated provider orders
          </p>
        </div>

        <button
          onClick={handleOpenCreateModal}
          className="btn btn-primary bg-emerald-600 hover:bg-emerald-700 border-none text-white shadow-sm flex items-center gap-2 rounded-xl"
        >
          <IconPlus size={20} stroke={iconStroke} />
          New Requisition
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white dark:bg-neutral-800 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-neutral-700 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {/* Search */}
          <div className="relative">
            <IconSearch className="absolute left-3 top-3 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search by Req #, dept, name..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              className="input input-bordered input-sm w-full pl-9 rounded-xl bg-gray-50 dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 text-sm"
            />
          </div>

          {/* Mode Filter */}
          <select
            value={selectedMode}
            onChange={(e) => {
              setSelectedMode(e.target.value);
              setPage(1);
            }}
            className="select select-bordered select-sm w-full rounded-xl bg-gray-50 dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 text-sm"
          >
            <option value="all">All Modes</option>
            <option value="self_managed">Self-Managed (Internal)</option>
            <option value="provider">Provider Integrated</option>
          </select>

          {/* Date Range: From */}
          <div className="flex items-center gap-1 bg-gray-50 dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded-xl px-2">
            <span className="text-xs text-gray-400">From:</span>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPage(1);
              }}
              className="bg-transparent border-none text-xs w-full py-1 text-gray-700 dark:text-gray-200 focus:outline-none"
            />
          </div>

          {/* Date Range: To */}
          <div className="flex items-center gap-1 bg-gray-50 dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded-xl px-2">
            <span className="text-xs text-gray-400">To:</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setPage(1);
              }}
              className="bg-transparent border-none text-xs w-full py-1 text-gray-700 dark:text-gray-200 focus:outline-none"
            />
          </div>
        </div>

        {/* Status Pill Filters */}
        <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-gray-100 dark:border-neutral-700">
          {[
            { id: "all", label: "All" },
            { id: "pending_approval", label: "Pending Approval" },
            { id: "approved", label: "Approved" },
            { id: "provider_ordered", label: "Provider Ordered" },
            { id: "provider_dispatched", label: "Dispatched" },
            { id: "received", label: "Received" },
            { id: "issued", label: "Issued" },
            { id: "rejected", label: "Rejected" },
            { id: "draft", label: "Drafts" },
          ].map((statusTab) => {
            const isActive = selectedStatus === statusTab.id;
            return (
              <button
                key={statusTab.id}
                onClick={() => {
                  setSelectedStatus(statusTab.id);
                  setPage(1);
                }}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                  isActive
                    ? "bg-emerald-600 text-white shadow-sm"
                    : "bg-gray-100 dark:bg-neutral-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-neutral-600"
                }`}
              >
                {statusTab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Requisitions List Table */}
      <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-sm border border-gray-100 dark:border-neutral-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full text-sm">
            <thead className="bg-gray-50/80 dark:bg-neutral-900/60 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
              <tr>
                <th className="py-3 px-4">Req #</th>
                <th className="py-3 px-4">Date</th>
                <th className="py-3 px-4">Mode</th>
                <th className="py-3 px-4">Department & Requester</th>
                <th className="py-3 px-4 text-center">Items</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">Bill #</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-neutral-700/60">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-gray-400">
                    <span className="loading loading-spinner loading-md text-emerald-600"></span>
                    <p className="text-xs mt-2">Loading requisitions...</p>
                  </td>
                </tr>
              ) : (!Array.isArray(requisitions) || requisitions.length === 0) ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-gray-400">
                    <IconClipboardList className="mx-auto mb-2 opacity-30" size={40} />
                    <p className="font-medium text-gray-600 dark:text-gray-300">No requisitions found</p>
                    <p className="text-xs mt-1">Create a new requisition to get started</p>
                  </td>
                </tr>
              ) : (
                (requisitions || []).map((req) => {
                  const statusInfo = STATUS_CONFIG[req.status] || {
                    label: req.status,
                    badge: "badge-ghost",
                    color: "text-gray-500",
                  };
                  const isProvider = req.mode === "provider";

                  return (
                    <tr key={req.id} className="hover:bg-gray-50/50 dark:hover:bg-neutral-700/30 transition-colors">
                      {/* Req Number */}
                      <td className="py-3 px-4">
                        <Link
                          to={`/dashboard/inventory/requisitions/${req.id}`}
                          className="font-semibold text-emerald-600 hover:underline flex items-center gap-1"
                        >
                          {req.requisition_no}
                        </Link>
                        {req.priority === "urgent" && (
                          <span className="badge badge-error badge-xs text-white uppercase mt-0.5 font-bold">
                            Urgent
                          </span>
                        )}
                        {req.priority === "high" && (
                          <span className="badge badge-warning badge-xs text-white uppercase mt-0.5">
                            High
                          </span>
                        )}
                      </td>

                      {/* Request Date */}
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <IconCalendar size={14} className="text-gray-400" />
                          <span>{formatDateSafe(req.request_date)}</span>
                        </div>
                        {req.needed_by_date && (
                          <div className="text-xs text-gray-400 mt-0.5">
                            Need: {formatDateSafe(req.needed_by_date)}
                          </div>
                        )}
                      </td>

                      {/* Mode */}
                      <td className="py-3 px-4">
                        {isProvider ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
                            <IconTruck size={12} />
                            Provider
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                            <IconBuildingWarehouse size={12} />
                            Self-Managed
                          </span>
                        )}
                      </td>

                      {/* Department & Requester */}
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-800 dark:text-gray-100">
                          {req.department || "General"}
                        </div>
                        <div className="text-xs text-gray-400">
                          By: {req.requested_by_name || req.requested_by}
                        </div>
                      </td>

                      {/* Items count */}
                      <td className="py-3 px-4 text-center">
                        <span className="badge badge-sm badge-neutral font-mono">
                          {req.item_count || 0}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className={`badge ${statusInfo.badge} badge-sm font-medium`}>
                          {statusInfo.label}
                        </span>
                      </td>

                      {/* Linked Purchase Bill */}
                      <td className="py-3 px-4 whitespace-nowrap text-xs">
                        {req.purchase_invoice_no ? (
                          <Link
                            to="/dashboard/inventory/purchases"
                            className="text-indigo-600 hover:underline flex items-center gap-1 font-mono"
                          >
                            <IconReceipt size={13} />
                            {req.purchase_invoice_no}
                          </Link>
                        ) : req.purchase_id ? (
                          <span className="font-mono text-gray-500">Bill #{req.purchase_id}</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="py-3 px-4 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1">
                          <Link
                            to={`/dashboard/inventory/requisitions/${req.id}`}
                            className="btn btn-sm btn-ghost btn-circle text-emerald-600 hover:bg-emerald-50 dark:hover:bg-neutral-700"
                            title="View Requisition"
                          >
                            <IconEye size={18} />
                          </Link>

                          {!["received", "issued", "cancelled"].includes(req.status) && (
                            <button
                              onClick={() => handleCancelRequisition(req.id, req.requisition_no)}
                              className="btn btn-sm btn-ghost btn-circle text-rose-500 hover:bg-rose-50 dark:hover:bg-neutral-700"
                              title="Cancel Requisition"
                            >
                              <IconX size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {Array.isArray(requisitions) && requisitions.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-4 p-4 border-t border-gray-100 dark:border-neutral-700 bg-gray-50/50 dark:bg-neutral-900/30">
            <div className="flex items-center gap-2 text-xs text-gray-500 font-medium">
              <span>
                Showing {total > 0 ? (page - 1) * pageSize + 1 : 0} to {Math.min(page * pageSize, Number(total) || 0)} of {Number(total) || 0} requisitions
              </span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="select select-xs select-bordered rounded-lg bg-white dark:bg-neutral-800 ml-2"
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
              </select>
            </div>

            <div className="join">
              <button
                className="join-item btn btn-xs"
                disabled={page <= 1}
                onClick={() => setPage(1)}
              >
                <IconChevronsLeft size={14} />
              </button>
              <button
                className="join-item btn btn-xs"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <IconChevronLeft size={14} />
              </button>
              <button className="join-item btn btn-xs no-animation font-semibold">
                Page {page} of {totalPages}
              </button>
              <button
                className="join-item btn btn-xs"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <IconChevronRight size={14} />
              </button>
              <button
                className="join-item btn btn-xs"
                disabled={page >= totalPages}
                onClick={() => setPage(totalPages)}
              >
                <IconChevronsRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create / Edit Requisition Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white dark:bg-neutral-800 rounded-3xl w-full max-w-4xl max-h-[92vh] flex flex-col shadow-2xl border border-gray-100 dark:border-neutral-700 animate-in fade-in duration-200 my-8">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-100 dark:border-neutral-700 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <IconClipboardList className="text-emerald-600" />
                  {editingRequisition ? "Edit Requisition" : "New Ingredient Requisition"}
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  Request ingredients from internal store or forward to provider
                </p>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="btn btn-sm btn-ghost btn-circle"
              >
                <IconX size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6 overflow-y-auto flex-1">
              {/* Mode Selection Cards */}
              <div>
                <label className="text-xs font-semibold uppercase text-gray-500 mb-2 block">
                  Requisition Mode *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div
                    onClick={() => setMode("self_managed")}
                    className={`cursor-pointer rounded-2xl p-4 border-2 transition-all ${
                      mode === "self_managed"
                        ? "border-emerald-600 bg-emerald-50/50 dark:bg-emerald-950/20"
                        : "border-gray-200 dark:border-neutral-700 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="req_mode"
                        checked={mode === "self_managed"}
                        onChange={() => setMode("self_managed")}
                        className="radio radio-primary radio-sm text-emerald-600"
                      />
                      <span className="font-semibold text-sm">Self-Managed (Internal Store)</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2 pl-6">
                      Approved ingredients are issued directly from the restaurant's internal inventory. No request sent to provider.
                    </p>
                  </div>

                  <div
                    onClick={() => setMode("provider")}
                    className={`cursor-pointer rounded-2xl p-4 border-2 transition-all ${
                      mode === "provider"
                        ? "border-purple-600 bg-purple-50/50 dark:bg-purple-950/20"
                        : "border-gray-200 dark:border-neutral-700 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="req_mode"
                        checked={mode === "provider"}
                        onChange={() => setMode("provider")}
                        className="radio radio-primary radio-sm text-purple-600"
                      />
                      <span className="font-semibold text-sm">Integrated Software Provider</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2 pl-6">
                      Forwarded to Software Provider Super Admin as a supply order. Stock updates only upon arrival & admin confirmation.
                    </p>
                  </div>
                </div>
              </div>

              {/* Department, Priority & Needed By */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">
                    Department
                  </label>
                  <select
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    className="select select-bordered select-sm w-full rounded-xl bg-gray-50 dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 text-sm"
                  >
                    <option value="Kitchen">Kitchen</option>
                    <option value="Bar">Bar</option>
                    <option value="Housekeeping">Housekeeping</option>
                    <option value="Bakery">Bakery</option>
                    <option value="Restaurant">Restaurant Front</option>
                    <option value="Store">General Store</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">
                    Priority
                  </label>
                  <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    className="select select-bordered select-sm w-full rounded-xl bg-gray-50 dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 text-sm"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">
                    Needed By Date
                  </label>
                  <input
                    type="date"
                    value={neededByDate}
                    onChange={(e) => setNeededByDate(e.target.value)}
                    className="input input-bordered input-sm w-full rounded-xl bg-gray-50 dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 text-sm"
                  />
                </div>
              </div>

              {/* Items Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold uppercase text-gray-500">
                    Requested Ingredients & Items ({items.length})
                  </label>
                  <button
                    type="button"
                    onClick={handleAddItemRow}
                    className="btn btn-xs btn-outline border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white rounded-lg flex items-center gap-1"
                  >
                    <IconPlus size={14} />
                    Add Row
                  </button>
                </div>

                <div className="space-y-2">
                  {items.map((itemRow, index) => (
                    <div
                      key={index}
                      className="flex flex-col sm:flex-row items-start sm:items-center gap-2 p-3 bg-gray-50 dark:bg-neutral-900/60 rounded-2xl border border-gray-100 dark:border-neutral-700"
                    >
                      {/* Ingredient Selector */}
                      <div className="flex-1 w-full sm:w-auto">
                        <Select
                          options={inventoryOptions}
                          value={itemRow.itemOption}
                          onChange={(opt) => handleItemSelect(index, opt)}
                          placeholder="Select Ingredient / Item..."
                          styles={selectStyles}
                          isClearable
                        />
                      </div>

                      {/* Quantity */}
                      <div className="w-full sm:w-28 flex items-center gap-1">
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          placeholder="Qty"
                          value={itemRow.requested_quantity}
                          onChange={(e) => handleItemChange(index, "requested_quantity", e.target.value)}
                          className="input input-bordered input-sm w-full rounded-xl bg-white dark:bg-neutral-800 text-sm"
                        />
                      </div>

                      {/* Unit (Display or edit) */}
                      <div className="w-full sm:w-24">
                        <input
                          type="text"
                          placeholder="Unit"
                          value={itemRow.requested_unit}
                          onChange={(e) => handleItemChange(index, "requested_unit", e.target.value)}
                          className="input input-bordered input-sm w-full rounded-xl bg-white dark:bg-neutral-800 text-sm"
                        />
                      </div>

                      {/* Item Note */}
                      <div className="w-full sm:w-44">
                        <input
                          type="text"
                          placeholder="Notes (optional)"
                          value={itemRow.notes}
                          onChange={(e) => handleItemChange(index, "notes", e.target.value)}
                          className="input input-bordered input-sm w-full rounded-xl bg-white dark:bg-neutral-800 text-xs"
                        />
                      </div>

                      {/* Remove Button */}
                      <button
                        type="button"
                        onClick={() => handleRemoveItemRow(index)}
                        className="btn btn-sm btn-ghost btn-circle text-rose-500 hover:bg-rose-50 dark:hover:bg-neutral-700 self-end sm:self-center"
                      >
                        <IconTrash size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* General Requester Notes */}
              <div>
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">
                  Additional Requester Notes / Instructions
                </label>
                <textarea
                  rows={2}
                  placeholder="Reason for requisition, urgency notes, special brands..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="textarea textarea-bordered w-full rounded-xl bg-gray-50 dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 text-sm"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-100 dark:border-neutral-700 flex flex-wrap items-center justify-between gap-3 bg-gray-50/50 dark:bg-neutral-900/30">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="btn btn-sm btn-ghost rounded-xl"
              >
                Cancel
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => handleSubmitRequisition(true)}
                  className="btn btn-sm btn-outline border-gray-300 rounded-xl"
                >
                  Save as Draft
                </button>

                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => handleSubmitRequisition(false)}
                  className="btn btn-sm btn-primary bg-emerald-600 hover:bg-emerald-700 border-none text-white rounded-xl shadow-sm flex items-center gap-1.5"
                >
                  {isSubmitting ? (
                    <span className="loading loading-spinner loading-xs"></span>
                  ) : (
                    <IconCheck size={16} />
                  )}
                  Submit for Approval
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
